import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  ButtonSelect,
  Icon,
  InlineMessage,
  InputField,
  Link as DSLink,
  Menu,
  MenuItem,
  type MenuOption,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import {
  CF_NEU_BLD,
  CF_SEC_MED,
  DEBOUNCE_TIME_FILTER_SEARCH,
  VIOLATIONS_TAB_LIST_AD_FILTER,
} from '../../../constants';
import { useDebounceForFunctions, useInvertedLocalize } from '../../../hooks';

import { CUSTOM_INPUTS } from './constants';
import { type SodCustomFilterProps } from './SodCustomFilter';
import {
  isEmptyInput,
  isFullInputAvailable,
  isOrderFollowed,
  isStrictNumeric,
} from './utils';

import './SodRiskScoreFilter.css';

const DEFAULT_CRITICAL: Record<string, string | boolean> = {
  state: false,
  message: '',
} as const;

interface FilterRefType extends HTMLDivElement {
  openDropdown: () => void;
}

export interface OptionsMap {
  options: MenuOption[];
  custom: {
    [key in (typeof CUSTOM_INPUTS)[keyof typeof CUSTOM_INPUTS]]: string;
  };
}

/**
 * SodRiskScoreFilter component allows users to filter SOD risk scores using a combination
 * of predefined options and a range input.
 *
 * @example
 * const options = [
 *   { label: 'Low Risk', value: 'low', isChecked: false },
 *   { label: 'Medium Risk', value: 'medium', isChecked: false },
 *   { label: 'High Risk', value: 'high', isChecked: false }
 * ];
 *
 * const handleOptionsClick = (filterKey, method, updatedOptions) => {
 *   console.log(filterKey, method, updatedOptions);
 * };
 *
 * return (
 *   <SodRiskScoreFilter
 *     filterKey="riskScore"
 *     method="BETWEEN"
 *     options={options}
 *     activeOptions={activeOptions}
 *     handleOptionsClick={handleOptionsClick}
 *     valueStart="0"
 *     valueEnd="100"
 *   />
 * );
 *
 * @param {SodRiskScoreFilterProps} props - The component props
 * @returns {React.ReactElement} A risk score filter component with options and range inputs
 */
function SodRiskScoreFilter({
  filter,
  activeOptions,
  // footerLink1Text,
  footerLink2Text,
  enabledFilterValue,
  setEnabledFilterValue,
  handleOptionsClick,
  // handleFilterChange,
  isDisabled,
}: SodCustomFilterProps): ReactElement {
  const l = useInvertedLocalize();

  const [isClearBtnDisabled, setIsClearBtnDisabled] = useState(true);
  const [optionsMap, setOptionsMap] = useState<OptionsMap>({
    options: filter.options ?? [],
    custom: {
      [CUSTOM_INPUTS.START]: '',
      [CUSTOM_INPUTS.END]: '',
    },
  });
  const [inputCritical, setInputCritical] = useState(DEFAULT_CRITICAL);

  const filterRef = useRef<FilterRefType | null>(null);

  const isViolationAgeFilter = useMemo(
    () =>
      Boolean(
        filter.value === VIOLATIONS_TAB_LIST_AD_FILTER.VIOLATIONAGE.value
      ),
    [filter.value]
  );

  useEffect(() => {
    const shouldOpenDropdown = Boolean(
      filterRef.current !== null &&
        enabledFilterValue &&
        filter.value === enabledFilterValue
    );

    if (shouldOpenDropdown && filterRef.current) {
      setTimeout(() => {
        filterRef.current?.openDropdown();

        if (setEnabledFilterValue) {
          setEnabledFilterValue(null);
        }
      }, 200);
    }
  }, [filter.value, enabledFilterValue, setEnabledFilterValue]);

  // Determine if  `Clear Selection` is enabled or not
  useEffect(() => {
    if (activeOptions.length === 0) {
      setIsClearBtnDisabled(true);

      return;
    }

    setIsClearBtnDisabled(false);
  }, [activeOptions]);

  // Manipulate options based on applied filters

  const getOptionLabel = useCallback(
    (value: string): string => {
      const [startValue, endValue] = JSON.parse(value) as number[];

      const label = l(
        isViolationAgeFilter
          ? '{startValue} to {endValue} days old'
          : '{startValue} to {endValue}',
        {
          startValue,
          endValue,
        }
      );

      return label;
    },
    [l, isViolationAgeFilter]
  );

  const alignOptionsWithSelection = useCallback(() => {
    const isCustomMatch = Boolean(
      !filter.options?.some((fa) =>
        activeOptions.some((fb) => fa.value === fb.value)
      ) && activeOptions.length > 0
    );

    const customOptions = JSON.parse(
      isCustomMatch ? activeOptions[0].value : '[null,null]'
    ) as (number | null)[];

    const [customStartValue, customEndValue] = customOptions;

    setOptionsMap((prev) => ({
      ...prev,
      options: prev.options.map((o) => ({
        ...o,
        label: o.label.trim().length === 0 ? getOptionLabel(o.value) : o.label,
        isChecked: activeOptions.some((ao) => ao.value === o.value),
      })),
      custom: {
        [CUSTOM_INPUTS.START]: String(
          customStartValue !== null ? customStartValue : ''
        ),
        [CUSTOM_INPUTS.END]: String(
          customEndValue !== null ? customEndValue : ''
        ),
      },
    }));
  }, [activeOptions, filter.options, getOptionLabel]);

  useEffect(() => {
    alignOptionsWithSelection();
  }, [alignOptionsWithSelection]);

  // Trigger option click
  const handleOptionsForCustomIp = useDebounceForFunctions(
    <T extends string | number | null>(start: T, end: T) => {
      const startLabel = start === null || start === '' ? '0' : String(start);
      const endLabel = end === null || end === '' ? '100' : String(end);
      const value = `[${start && start !== '' ? start : 'null'},${
        end && end !== '' ? end : 'null'
      }]`;

      handleOptionsClick(filter.value, filter.method, [
        {
          label: l('{startValue} to {endValue} days old', {
            startValue: startLabel,
            endValue: endLabel,
          }),
          value,
          isChecked: true,
        },
      ]);
    },
    DEBOUNCE_TIME_FILTER_SEARCH
  );

  // Input validation
  const handleTextChange = <T extends string>(name: T, value: T) => {
    setInputCritical(() => DEFAULT_CRITICAL);

    // Check if value is numeric
    const isNumber = isStrictNumeric(value);

    setOptionsMap((prev) => ({
      ...prev,
      custom: {
        ...prev.custom,
        [name]: value,
      },
    }));

    if (!isNumber) {
      setInputCritical(() => ({
        state: true,
        message: l('Value should be a number'),
      }));

      return;
    }

    // Check if value falls in range
    if (Number(value) < 0) {
      setInputCritical(() => ({
        state: true,
        message: l('Value should be greater than 0'),
      }));

      return;
    }

    if (!isViolationAgeFilter && Number(value) > 100) {
      setInputCritical(() => ({
        state: true,
        message: l('Value should be less than 100'),
      }));

      return;
    }

    const latestOptionMap = {
      ...optionsMap,
      custom: {
        ...optionsMap.custom,
        [name]: value,
      },
    };

    // Check if both numbers are negetive
    if (
      Number(latestOptionMap.custom[CUSTOM_INPUTS.END]) < 0 ||
      Number(latestOptionMap.custom[CUSTOM_INPUTS.START]) < 0
    ) {
      setInputCritical(() => ({
        state: true,
        message: l('Value should be greater than 0'),
      }));

      return;
    }

    // Check for empty input
    if (isEmptyInput(latestOptionMap)) {
      setInputCritical({
        state: true,
        message: l('Both start and end values are required'),
      });

      return;
    }

    // Check if start is smaller than end
    if (!isOrderFollowed(name, Number(value), latestOptionMap)) {
      setInputCritical({
        state: true,
        message: 'Start value should be less than end value',
      });

      return;
    }

    if (!isFullInputAvailable(name, latestOptionMap)) {
      return;
    }

    if (name === CUSTOM_INPUTS.START) {
      handleOptionsForCustomIp(value, optionsMap.custom[CUSTOM_INPUTS.END]);

      return;
    }

    handleOptionsForCustomIp(optionsMap.custom[CUSTOM_INPUTS.START], value);
  };

  const inputWrapperClasses = cx(
    'SodRiskScoreFilter-inputFieldWrapper',
    isViolationAgeFilter && 'SodRiskScoreFilter-inputFieldWrapper--isAgeFilter'
  );

  const getCustomLabel = () => {
    const { label } = filter;

    if (activeOptions.length !== 0) {
      // Normal operations resume
      const labelArray = activeOptions
        .map((o) => getOptionLabel(o.value))
        .join(', ');
      const labelText = `${label}: `;

      return (
        <Typography
          kind='body2'
          desktop='body2-d'
          mobile='body2-d'
          color={CF_SEC_MED}>
          <span className='SodCustomFilter-btnLabel'>{labelText}</span>
          {labelArray}
        </Typography>
      );
    }

    return label;
  };

  const getFilterFooter = () => (
    <Box tag='div' className='SodRiskScoreFilter-footer'>
      <div />
      <DSLink
        text={footerLink2Text}
        isDisabled={isClearBtnDisabled}
        size='small'
        kind='button'
        onClick={() => {
          handleOptionsClick(filter.value, filter.method, []);
        }}
      />
    </Box>
  );

  return (
    <Menu
      ref={filterRef}
      key={filter.value}
      setIsExpanded={(isExpanded) => {
        if (!isExpanded) {
          setInputCritical(() => DEFAULT_CRITICAL);
          alignOptionsWithSelection();
        }
      }}
      CustomMenu={
        <>
          <ul role='listbox' tabIndex={-1} className='MenuMulti-items'>
            {optionsMap.options.map((o) => (
              <MenuItem
                key={o.value}
                option={{
                  label: o.label,
                  value: o.value,
                  isChecked: o.isChecked,
                }}
                onClick={() => {
                  setInputCritical(DEFAULT_CRITICAL);

                  handleOptionsClick(filter.value, filter.method, [
                    {
                      label: o.label,
                      value: o.value,
                      isChecked: true,
                    },
                  ]);
                }}
                tabIndex={0}
              />
            ))}
            <Box tag='div' className={inputWrapperClasses}>
              <InputField
                name={CUSTOM_INPUTS.START}
                placeholder={l('Type number')}
                kind='search'
                backgroundColor='neutral'
                size='medium'
                value={optionsMap.custom[CUSTOM_INPUTS.START]}
                setValue={(text) => {
                  handleTextChange(CUSTOM_INPUTS.START, text);
                }}
                shouldDisallowBrackets
                isResetButtonVisible={false}
                className='SodRiskScoreFilter-inputField'
              />
              <Typography
                kind='body3'
                desktop='body3-d'
                mobile='body3-d'
                color={CF_NEU_BLD}>
                {l('to')}
              </Typography>
              <InputField
                name={CUSTOM_INPUTS.END}
                placeholder={l('Type number')}
                kind='search'
                backgroundColor='neutral'
                size='medium'
                value={optionsMap.custom[CUSTOM_INPUTS.END]}
                setValue={(text) => {
                  handleTextChange(CUSTOM_INPUTS.END, text);
                }}
                shouldDisallowBrackets
                isResetButtonVisible={false}
                className='SodRiskScoreFilter-inputField'
              />
              {isViolationAgeFilter && (
                <Typography
                  kind='body2'
                  desktop='body2-d'
                  mobile='body2-d'
                  color={CF_NEU_BLD}>
                  {l('days old')}
                </Typography>
              )}
            </Box>
            {inputCritical.state ? (
              <InlineMessage
                text={inputCritical.message as string}
                className='SodRiskScoreFilter-warning'
                colorTheme='critical'
                size='medium'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            ) : null}
          </ul>
          {getFilterFooter()}
        </>
      }
      label={filter.label}
      trigger={
        <ButtonSelect
          kind='pill'
          label={getCustomLabel()}
          isDisabled={isDisabled}
        />
      }
    />
  );
}

SodRiskScoreFilter.defaultProps = {
  enabledFilterValue: null,
  setEnabledFilterValue: () => {},
  isDisabled: false,
};

export default memo(SodRiskScoreFilter);
